import { useState, useRef, useEffect, useCallback } from 'react';
let globalCounter = 0;
/** For "silent" mode: generate a muted audio track */
function createSilentAudioTrack() {
    const audioCtx = new AudioContext();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    gainNode.gain.value = 0;
    oscillator.connect(gainNode);
    const dest = audioCtx.createMediaStreamDestination();
    gainNode.connect(dest);
    oscillator.start();
    return dest.stream.getAudioTracks()[0];
}
export function createConnectionController(config) {
    return {
        useProps(props) {
            // 1) Helper debug functions that only log if props.debug === true
            const debugLog = (...args) => {
                if (props.debug) {
                    console.log(...args);
                }
            };
            const debugWarn = (...args) => {
                if (props.debug) {
                    console.warn(...args);
                }
            };
            const debugError = (...args) => {
                if (props.debug) {
                    console.error(...args);
                }
            };
            /**
             * Log every state transition in one place (only if debug).
             */
            function logStateTransition(prev, next, reason) {
                if (!props.debug)
                    return;
                if (prev.status !== next.status) {
                    debugLog(`[STATE] ${prev.status} -> ${next.status} (Reason: ${reason})`);
                }
                else {
                    debugLog(`[STATE] ${prev.status} => ${next.status} (unchanged, Reason: ${reason})`);
                }
            }
            /**
             * Our internal state + a custom setState that logs transitions.
             */
            const [internalState, rawSetState] = useState(() => {
                // Decide initial status:
                if (props.disabled) {
                    const s = { status: 'closed' };
                    debugLog('[INIT] Starting in CLOSED because disabled === true');
                    return s;
                }
                else {
                    const s = { status: 'pending' };
                    debugLog('[INIT] Starting in PENDING (not disabled)');
                    return s;
                }
            });
            function setStateWithLog(updater, reason) {
                rawSetState((prev) => {
                    const next = updater(prev);
                    logStateTransition(prev, next, reason);
                    return next;
                });
            }
            const state = internalState;
            // Default grace period
            const gracePeriod = config.closeGracePeriodMs ?? 5000;
            // Refs for peer connection, data channel, local stream
            const pcRef = useRef(null);
            const dcRef = useRef(null);
            const localStreamRef = useRef(null);
            // Callback refs
            const onReadyRef = useRef(props.onReady);
            const onErrorRef = useRef(props.onError);
            const onCloseRef = useRef(props.onClose);
            useEffect(() => {
                onReadyRef.current = props.onReady;
            }, [props.onReady]);
            useEffect(() => {
                onErrorRef.current = props.onError;
            }, [props.onError]);
            useEffect(() => {
                onCloseRef.current = props.onClose;
            }, [props.onClose]);
            /**
             * The tearDown function, with a safeguard:
             * If we're already closed, we skip re-tearing down.
             */
            function tearDown(options) {
                debugLog(`[tearDown] Called with finalClose=${!!options?.finalClose}, currentState=${state.status}`);
                // SAFEGUARD: If we're already closed, stop here!
                if (state.status === 'closed') {
                    debugLog('[tearDown] Already CLOSED. Skipping tearDown.');
                    return;
                }
                // Stop local tracks
                if (localStreamRef.current) {
                    localStreamRef.current.getTracks().forEach((track) => track.stop());
                    localStreamRef.current = null;
                }
                // Close data channel
                if (dcRef.current) {
                    try {
                        dcRef.current.close();
                        debugLog('[tearDown] DataChannel closed');
                    }
                    catch { }
                    dcRef.current = null;
                }
                // Close peer connection
                if (pcRef.current) {
                    try {
                        pcRef.current.close();
                        debugLog('[tearDown] PeerConnection closed');
                    }
                    catch { }
                    pcRef.current = null;
                }
                // Final state
                if (options?.finalClose) {
                    setStateWithLog((prev) => ({ ...prev, status: 'closed', error: undefined }), 'tearDown(finalClose=true)');
                }
                else {
                    setStateWithLog((prev) => ({ ...prev, status: 'disconnected', error: undefined }), 'tearDown(finalClose=false)');
                }
            }
            /**
             * On unmount => tear down to avoid leaks
             */
            useEffect(() => {
                debugLog('[useEffect:unmount] Adding unmount cleanup');
                return () => {
                    debugLog('[useEffect:unmount] Component unmounted => tearDown');
                    tearDown({ finalClose: false });
                };
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, []);
            /**
             * Detect offline/online events, attach if not disabled/closed
             */
            useEffect(() => {
                debugLog(`[useEffect:offline/online] Checking if we attach net listeners. disabled=${props.disabled}, status=${state.status}`);
                if (props.disabled || state.status === 'closed') {
                    debugLog('[useEffect:offline/online] Skipping because disabled or closed');
                    return;
                }
                function handleOnline() {
                    debugLog('[NET] We got an "online" event');
                    setStateWithLog((prev) => {
                        if (prev.status === 'disconnected') {
                            return { ...prev, status: 'pending', error: undefined };
                        }
                        return prev;
                    }, 'handleOnline');
                }
                function handleOffline() {
                    debugLog('[NET] We got an "offline" event');
                    setStateWithLog((prev) => {
                        if (prev.status !== 'closed') {
                            return { ...prev, status: 'disconnected', error: undefined };
                        }
                        return prev;
                    }, 'handleOffline');
                }
                window.addEventListener('online', handleOnline);
                window.addEventListener('offline', handleOffline);
                return () => {
                    debugLog('[useEffect:offline/online] Removing net listeners');
                    window.removeEventListener('online', handleOnline);
                    window.removeEventListener('offline', handleOffline);
                };
            }, [props.disabled, state.status]);
            /**
             * -------------
             * IMPORTANT: If the 'mode' changes while connected, we want to
             * re-init with the new mode. We'll do that by forcing status='pending'.
             * -------------
             */
            const lastModeRef = useRef(props.mode);
            useEffect(() => {
                if (lastModeRef.current !== props.mode) {
                    // The mode changed from something to something else
                    debugLog(`[modeChangeEffect] Mode changed from ${lastModeRef.current} to ${props.mode}`);
                    lastModeRef.current = props.mode;
                    // If we are actively connected/connecting,
                    // we'll tear down and reset to 'pending' so we reinit with new mode
                    const activeStates = [
                        'connecting',
                        'reconnecting',
                        'connected',
                        'closing',
                    ];
                    if (activeStates.includes(state.status)) {
                        debugLog('[modeChangeEffect] Tearing down and forcing status=pending for new mode');
                        tearDown();
                        setStateWithLog((prev) => ({ ...prev, status: 'pending', error: undefined }), 'mode changed => reinit');
                    }
                    else if (state.status === 'disconnected') {
                        // If 'disconnected', just go 'pending' to re-init next render
                        debugLog('[modeChangeEffect] We are disconnected => set status=pending');
                        setStateWithLog((prev) => ({ ...prev, status: 'pending', error: undefined }), 'mode changed => reinit from disconnected');
                    }
                    // If we were 'closed' or 'error' or 'pending' already,
                    // we do nothing special. The main effect or user can handle it.
                }
            }, [props.mode, state.status]);
            /**
             * -------------
             * IMPORTANT: If the 'token' changes while connected, we want to
             * re-init with the new token. We'll do that by forcing status='pending'.
             * -------------
             */
            // Track token with a ref so we can detect changes directly
            const tokenRef = useRef(null);
            // Use a manual "force reconnect" counter to ensure reconnection happens
            const [forceReconnect, setForceReconnect] = useState(0);
            // Detect token changes
            useEffect(() => {
                // Only proceed if we have a token and are not in 'closed' state
                if (!props.token || state.status === 'closed') {
                    debugLog(`[tokenChangeEffect] No token or closed state (${state.status}), skipping`);
                    return;
                }
                const currentToken = props.token;
                const tokenPreview = currentToken
                    ? `...${currentToken.slice(-6)}`
                    : 'none';
                // Check if this is an actual token change
                if (tokenRef.current !== currentToken) {
                    debugLog(`[tokenChangeEffect] Token changed to: ${tokenPreview}`);
                    // Update our reference
                    tokenRef.current = currentToken;
                    // Only handle reconnection if we're already connected
                    if (['connected', 'connecting', 'reconnecting'].includes(state.status)) {
                        debugLog('[tokenChangeEffect] Active connection with token change, forcing reconnect');
                        // First tear down existing connection
                        tearDown();
                        // Then force to pending state
                        setStateWithLog((prev) => ({ ...prev, status: 'pending', error: undefined }), 'token changed => reinit');
                        // Force a reconnection by incrementing counter
                        setForceReconnect((prev) => prev + 1);
                    }
                }
                else {
                    debugLog(`[tokenChangeEffect] Using token: ${tokenPreview} (no change)`);
                }
            }, [props.token, state.status]);
            /**
             * Main effect: if not disabled => connect; if disabled => graceful close
             */
            useEffect(() => {
                let canceled = false;
                let graceTimer = null;
                const currentStatus = state.status;
                debugLog(`[useEffect:main] Start. status=${currentStatus}, disabled=${props.disabled}, forceReconnect=${forceReconnect}`);
                // A) If 'closed', do nothing
                if (currentStatus === 'closed') {
                    debugLog('[useEffect:main] Status=closed => no action');
                    return;
                }
                // B) If disabled => graceful close if connected
                if (props.disabled) {
                    debugLog('[useEffect:main] disabled=true => maybe closing');
                    const activeStates = ['connecting', 'reconnecting', 'connected'];
                    if (activeStates.includes(currentStatus)) {
                        if (currentStatus !== 'closing') {
                            debugLog('[useEffect:main] moving to CLOSING');
                            setStateWithLog((prev) => ({
                                ...prev,
                                status: 'closing',
                                error: undefined,
                            }), 'disabled => set closing');
                        }
                        debugLog(`[useEffect:main] Grace timer => ${gracePeriod}ms`);
                        graceTimer = setTimeout(() => {
                            if (!canceled) {
                                debugLog('[useEffect:main] graceTimer => finalClose');
                                tearDown({ finalClose: true });
                                onCloseRef.current?.();
                            }
                        }, gracePeriod);
                    }
                    else {
                        // If pending/disconnected/error/closing => finalize immediately
                        debugLog('[useEffect:main] Not actively connected => finalClose now');
                        tearDown({ finalClose: true });
                        onCloseRef.current?.();
                    }
                    return () => {
                        canceled = true;
                        if (graceTimer) {
                            debugLog('[useEffect:main] cleanup => clear graceTimer');
                            clearTimeout(graceTimer);
                        }
                    };
                }
                // C) If not disabled => connect if status in [pending, disconnected, error]
                if (['pending', 'disconnected', 'error'].includes(currentStatus)) {
                    debugLog('[useEffect:main] Need to connect => tearDown old + set connecting');
                    tearDown();
                    setStateWithLog((prev) => ({ ...prev, status: 'connecting', error: undefined }), 'initiating connect flow');
                    const pc = new RTCPeerConnection();
                    pcRef.current = pc;
                    const dc = pc.createDataChannel(`oai-events-${globalCounter++}`);
                    dcRef.current = dc;
                    debugLog('[useEffect:main] Created new RTCPeerConnection + DataChannel');
                    dc.onopen = () => {
                        if (canceled)
                            return;
                        debugLog('[DataChannel] onopen => status=connected');
                        setStateWithLog((prev) => ({
                            ...prev,
                            status: 'connected',
                            error: undefined,
                        }), 'dc.onopen');
                        onReadyRef.current?.();
                    };
                    dc.onclose = () => {
                        if (canceled)
                            return;
                        debugLog('[DataChannel] onclose => status=disconnected');
                        setStateWithLog((prev) => ({
                            ...prev,
                            status: 'disconnected',
                            error: undefined,
                        }), 'dc.onclose');
                        onCloseRef.current?.();
                    };
                    dc.onerror = (event) => {
                        if (canceled)
                            return;
                        debugLog('[DataChannel] onerror => status=error', event.error);
                        setStateWithLog((prev) => ({ ...prev, status: 'error', error: event.error }), 'dc.onerror');
                        onErrorRef.current?.(event.error);
                    };
                    (async () => {
                        try {
                            let localStream;
                            if (props.mode === 'audio') {
                                debugLog('[useEffect:main] requesting userMedia audio');
                                localStream = await navigator.mediaDevices.getUserMedia({
                                    audio: true,
                                });
                            }
                            else {
                                debugLog('[useEffect:main] using createSilentAudioTrack');
                                localStream = new MediaStream([createSilentAudioTrack()]);
                            }
                            if (canceled) {
                                debugLog('[useEffect:main] canceled while acquiring localStream');
                                localStream.getTracks().forEach((t) => t.stop());
                                return;
                            }
                            localStreamRef.current = localStream;
                            debugLog('[useEffect:main] localStream acquired, add tracks');
                            localStream.getTracks().forEach((track) => {
                                if (canceled || pc.connectionState === 'closed') {
                                    track.stop();
                                    throw new Error('PeerConnection closed before addTrack');
                                }
                                pc.addTrack(track, localStream);
                            });
                            pc.ontrack = (evt) => {
                                const remoteStream = evt.streams[0];
                                debugLog('[PeerConnection] ontrack => got remote stream');
                                if (remoteStream) {
                                    const audio = new Audio();
                                    audio.autoplay = true;
                                    audio.srcObject = remoteStream;
                                }
                            };
                            debugLog('[useEffect:main] creating offer...');
                            const offer = await pc.createOffer();
                            await pc.setLocalDescription(offer);
                            debugLog('[useEffect:main] localDesc set. posting offer to server');
                            // Example POST
                            const response = await fetch(config.endpoint, {
                                method: 'POST',
                                body: offer.sdp,
                                headers: {
                                    Authorization: `Bearer ${props.token}`,
                                    'Content-Type': 'application/sdp',
                                },
                            });
                            const remoteSdp = await response.text();
                            const answer = { type: 'answer', sdp: remoteSdp };
                            if (!canceled && pc.connectionState !== 'closed') {
                                debugLog('[useEffect:main] setRemoteDescription w/answer');
                                await pc.setRemoteDescription(answer);
                                debugLog('[useEffect:main] remoteDescription set');
                            }
                            else {
                                debugLog('[useEffect:main] canceled or pc closed before setRemote');
                            }
                        }
                        catch (err) {
                            if (!canceled) {
                                debugLog('[useEffect:main] error connecting PeerConnection:', err);
                                setStateWithLog((prev) => ({ ...prev, status: 'error', error: err }), 'error setting remoteDescription or userMedia');
                                onErrorRef.current?.(err);
                            }
                        }
                    })();
                }
                else {
                    debugLog(`[useEffect:main] status=${currentStatus}, skipping connect logic`);
                }
                return () => {
                    canceled = true;
                    if (graceTimer) {
                        debugLog('[useEffect:main] cleanup => clearing graceTimer');
                        clearTimeout(graceTimer);
                    }
                };
            }, [props.disabled, props.mode, props.token, gracePeriod, forceReconnect]);
            /**
             * The function to send data over the data channel
             */
            const sendPayload = useCallback((data) => {
                const curStatus = state.status;
                if (curStatus === 'connected' &&
                    dcRef.current?.readyState === 'open') {
                    try {
                        debugLog('[sendPayload] sending user data:', data);
                        dcRef.current.send(JSON.stringify(data));
                    }
                    catch (err) {
                        debugError('[sendPayload] failed to send JSON payload:', err);
                    }
                }
                else {
                    debugWarn(`[sendPayload] ignoring user input: status='${curStatus}' or DC not open.`);
                }
            }, [state]);
            // Return shape
            return {
                dataChannel: dcRef.current,
                state,
                sendPayload,
            };
        },
    };
}
//# sourceMappingURL=connectionController.js.map